import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ArrowRight, GripVertical, Check, Trash2, Plus, Search } from 'lucide-react';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

export function QuickStat({ icon: Icon, label, value, color, onClick }) {
  const Component = onClick ? 'button' : 'div';
  return (
    <Component 
      onClick={onClick}
      className={`bg-white/5 backdrop-blur-xl rounded-2xl border border-white/10 p-4 text-left w-full ${
        onClick ? 'cursor-pointer hover:bg-white/10 transition-all group' : ''
      }`}
    >
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: color + '20' }}>
          <Icon className="w-5 h-5" style={{ color }} />
        </div>
        <div className="flex-1">
          <p className="text-white/50 text-xs">{label}</p>
          <p className="text-white font-semibold text-lg">{value}</p>
        </div>
        {onClick && <ArrowRight className="w-4 h-4 text-white/30 opacity-0 group-hover:opacity-100 transition-opacity" />}
      </div>
    </Component>
  );
}

export function BentoCard({ title, icon: Icon, color, children, onClick }) {
  return (
    <div
      onClick={onClick}
      className="bg-white/5 backdrop-blur-xl rounded-2xl border border-white/10 p-5 cursor-pointer hover:bg-white/10 transition-all group"
    >
      <div className="flex items-center gap-3 mb-3">
        <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: color + '20' }}>
          <Icon className="w-4 h-4" style={{ color }} />
        </div>
        <span className="text-white/60 text-sm font-medium">{title}</span>
        <ArrowRight className="w-4 h-4 text-white/30 ml-auto opacity-0 group-hover:opacity-100 transition-opacity" />
      </div>
      {children}
    </div>
  );
}

export function SectionHeader({ title, icon: Icon, color }) {
  return (
    <div className="flex items-center gap-3">
      <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: color + '20' }}>
        <Icon className="w-5 h-5" style={{ color }} />
      </div>
      <h2 className="text-2xl font-bold text-white">{title}</h2>
    </div>
  );
}

export function InfoItem({ label, value, className = '' }) {
  return (
    <div className={className}>
      <p className="text-white/40 text-xs uppercase tracking-wider mb-1">{label}</p>
      <p className="text-white">{value}</p>
    </div>
  );
}

export function EmptyState({ icon: Icon, title, description, action, color }) {
  return (
    <div className="text-center py-12">
      <div className="w-16 h-16 mx-auto mb-4 rounded-2xl flex items-center justify-center" style={{ backgroundColor: color + '20' }}>
        <Icon className="w-8 h-8" style={{ color }} />
      </div>
      <h3 className="text-white font-medium mb-2">{title}</h3>
      <p className="text-white/50 text-sm mb-4">{description}</p>
      {action && (
        <Button onClick={action.onClick} style={{ backgroundColor: color }} className="text-white">
          {action.label}
        </Button>
      )}
    </div>
  );
}

export function PaymentScheduleItem({ label, amount, dueDate, isPaid, onPayClick, paymentEnabled, primaryColor }) {
  return (
    <div 
      className={`flex items-center justify-between p-4 rounded-xl transition-all ${
        isPaid 
          ? 'bg-green-500/10 border border-green-500/30' 
          : 'bg-white/5 border border-white/10 hover:bg-white/10'
      }`}
      data-testid={`payment-schedule-item-${label.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <div>
        <p className="text-white font-medium">{label}</p>
        {dueDate && <p className="text-white/50 text-xs mt-1">Due: {dueDate}</p>}
      </div>
      <div className="flex items-center gap-3">
        <span className={`font-semibold ${isPaid ? 'text-green-400' : 'text-white'}`}>
          ${typeof amount === 'number' ? amount.toFixed(2) : amount}
        </span>
        {isPaid ? (
          <div className="w-8 h-8 rounded-lg bg-green-500/20 flex items-center justify-center">
            <Check className="w-4 h-4 text-green-400" />
          </div>
        ) : paymentEnabled ? (
          <Button 
            size="sm" 
            onClick={() => onPayClick(amount, label)} 
            style={{ backgroundColor: primaryColor }}
            className="text-white text-xs"
            data-testid={`pay-button-${label.toLowerCase().replace(/\s+/g, '-')}`}
          >
            Pay
          </Button>
        ) : null}
      </div>
    </div>
  );
}
